import { useInfiniteQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getReconciliationsProducts } from "../../../../../services/inventory";
import { useContext, useState, useMemo } from "react";
import { ToastContext } from "../../../../../hooks/context";
import {
  useApproveReconciliation,
  useRejectReconciliation,
} from "../../../../../hooks/api/mutations/useInventory";
import { useGetInfo } from "../../../../../hooks/useGetInfo";
import {
  useGetReconciliationDetails,
  useGetReconciliationStockHistory,
} from "../../../../../hooks/api/queries";
import { useUrlSearch } from "../../../../../hooks/useUrlSearch";

const useReconciliationReview = ({ isEnabledDetails = true }) => {
  const { id } = useParams();
  const { search, gotoPage } = useUrlSearch();
  const [reconciliedData, setReconciliedData] = useState({});
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const [isReconciliationPrompt, setIsReconciliationPrompt] = useState(false);
  const [reconciliationType, setReconciliationType] = useState(null);
  const [isReview] = useState(true);
  const store_id = search.get("store_id");
  const { getStoreInfo } = useGetInfo();
  const storeDetails = getStoreInfo(store_id);
  const queryClient = useQueryClient();

  const {
    isLoading: isFetchingReconciliationDetails,
    data: reconciliationDetails,
  } = useGetReconciliationDetails({
    id,
    isEnabled: isEnabledDetails,
  });

  const { isLoading: isLoadingStockHistory, data: stockHistoryData } =
    useGetReconciliationStockHistory({
      id,
      isEnabled: isEnabledDetails,
    });

  const reconciliationStockHistory = stockHistoryData?.data || [];
  const metaDetails = stockHistoryData?.meta || {};

  const { isLoading, data, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery(
      ["getReconciliationsProducts", [id, isReview]],
      ({ pageParam = 1 }) =>
        getReconciliationsProducts({
          id: id,
          page: pageParam,
          review: isReview,
        }).then((res) => res?.data),
      {
        cacheTime: 0,
        getNextPageParam: (lastPage) => lastPage?.meta?.next_page ?? undefined,
      },
    );

  const productsList = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page?.data ?? []);
  }, [data?.pages]);

  const { mutate: approve, isLoading: isApproving } =
    useApproveReconciliation();
  const approveReconciledData = () => {
    approve(
      { id: id },
      {
        onSuccess: (res) => {
          triggerToast(res?.data?.detail, "success");
          queryClient.invalidateQueries(["getReconciliationDetails", `${id}`]);
          navigate(-1);
        },
      },
    );
  };

  const { mutate: reject, isLoading: isRejecting } = useRejectReconciliation();
  const rejectReconciledData = () => {
    reject(
      { id: id },
      {
        onSuccess: (res) => {
          triggerToast(res?.data?.detail, "success");
          queryClient.invalidateQueries(["getReconciliationDetails", `${id}`]);
          navigate(-1);
        },
      },
    );
  };

  const proceedReconciliation = () => {
    if (reconciliationType === "approve") approveReconciledData();
    else rejectReconciledData();
  };

  return {
    storeDetails,
    isFetchingReconciliationDetails,
    reconciliationDetails,
    isRejecting,
    isApproving,
    products: productsList || [],
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    reconciliedData,
    reconciliationType,
    isReconciliationPrompt,
    isLoadingStockHistory,
    reconciliationStockHistory,
    metaDetails,
    gotoPage,
    autoScrollRef: null,
    fetchNextPage,
    approveReconciledData,
    rejectReconciledData,
    setReconciliedData,
    setIsReconciliationPrompt,
    proceedReconciliation,
    setReconciliationType,
  };
};
export default useReconciliationReview;
